// components/ServerDatabase/ServerFilter.jsx
import React, { useEffect, useState } from 'react';

// Added currentView and onViewChange props
const ServerFilter = ({ onFilterChange, currentFilters, currentView, onViewChange }) => {
    const [search, setSearch] = useState(currentFilters.search || '');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [version, setVersion] = useState(currentFilters.version || '');
    const [playerCount, setPlayerCount] = useState(currentFilters.playerCount || '');
    const [lastTested, setLastTested] = useState(currentFilters.lastTested || '');

    useEffect(() => {
        setSearch(currentFilters.search || '');
        setVersion(currentFilters.version || '');
        setPlayerCount(currentFilters.playerCount || '');
        setLastTested(currentFilters.lastTested || '');
    }, [currentFilters]);

    const filters = [
        { id: 'all', label: 'All Servers', icon: 'fa-server', color: 'accent-blue' },
        { id: 'online', label: 'Online', icon: 'fa-plug', color: 'accent-green' },
        { id: 'popular', label: 'Popular', icon: 'fa-fire', color: 'accent-orange' },
        { id: 'vulnerable', label: 'Vulnerable', icon: 'fa-bug', color: 'accent-red' }
    ];

    const handleStatusFilter = (filterId) => {
        onFilterChange({ ...currentFilters, status: filterId, search: search });
    };

    const handleSearchChange = (e) => {
        const newSearch = e.target.value;
        setSearch(newSearch);
        // Debounce this in a real app
        onFilterChange({ ...currentFilters, search: newSearch });
    };

    const handleAdvancedFilterChange = (filterName, value) => {
        if (filterName === 'version') setVersion(value);
        if (filterName === 'players') setPlayerCount(value);
        if (filterName === 'tested') setLastTested(value);
        onFilterChange({ ...currentFilters, [filterName]: value, search: search });
    };

    return (
        <div className="mb-6 bg-dark-600 rounded-2xl p-5 hover:shadow-md transition-medium border border-dark-400 hover:border-accent-blue/30 transform-gpu hover:scale-[1.005]">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Status Filters */}
                <div className="flex flex-wrap gap-2">
                    {filters.map((filter) => (
                        <button
                            key={filter.id}
                            onClick={() => handleStatusFilter(filter.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center transition-medium transform-gpu ${currentFilters.status === filter.id ? `bg-${filter.color}/20 text-${filter.color} hover:bg-${filter.color}/30 scale-[1.03]` : 'bg-dark-500 hover:bg-dark-400 hover:-translate-y-0.5'
                                }`}
                        >
                            <i className={`fas ${filter.icon} mr-2 text-xs`}></i> {filter.label}
                        </button>
                    ))}
                    <button className="bg-dark-500 hover:bg-dark-400 px-3 py-1.5 rounded-lg text-sm font-medium transition-medium transform-gpu hover:-translate-y-0.5" onClick={() => setShowAdvanced(!showAdvanced)} aria-expanded={showAdvanced}>
                        <i className="fas fa-filter mr-1"></i> More Filters <i className={`fas fa-chevron-${showAdvanced ? 'up' : 'down'} ml-2 text-xs`}></i>
                    </button>
                </div>
                {/* View Toggle Buttons */}
                <div className="flex items-center gap-2 self-start md:self-center">
                    <button
                        onClick={() => onViewChange('grid')} // Call onViewChange
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-medium transform-gpu hover:rotate-12 ${currentView === 'grid' ? 'bg-accent-purple/20 text-accent-purple hover:bg-accent-purple/30 hover:shadow-glow' : 'bg-dark-500 hover:bg-dark-400'
                            }`}
                        aria-label="Grid View"
                        title="Grid View"
                    > <i className="fas fa-th-large"></i> </button>
                    <button
                        onClick={() => onViewChange('list')} // Call onViewChange
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-medium transform-gpu hover:rotate-12 ${currentView === 'list' ? 'bg-accent-purple/20 text-accent-purple hover:bg-accent-purple/30 hover:shadow-glow' : 'bg-dark-500 hover:bg-dark-400'
                            }`}
                        aria-label="List View"
                        title="List View"
                    > <i className="fas fa-list"></i> </button>
                </div>
            </div>

            {/* Search Input */}
            <div className="mt-4 transform-gpu transition-medium hover:scale-[1.01]">
                <label htmlFor="server-search" className="sr-only">Search Servers</label>
                <div className="relative">
                    <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 peer-focus:text-accent-purple transition-colors"></i>
                    <input id="server-search" type="text" placeholder="Search servers by IP, name, or tag..." value={search} onChange={handleSearchChange} className="peer w-full bg-dark-500 rounded-xl py-2 px-4 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/50 border border-dark-400 hover:border-dark-300 transition-medium" />
                </div>
            </div>

            {/* Advanced Filters */}
            {showAdvanced && (
                <div className="mt-4 pt-4 border-t border-dark-400 animate-fade-in">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {/* Version */}
                        <div className="transform-gpu transition-medium hover:scale-[1.02]">
                            <label htmlFor="filter-version" className="block text-xs text-gray-400 mb-1">Version</label>
                            <select id="filter-version" className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-blue/50 hover:border-dark-300 transition-medium" value={version} onChange={(e) => handleAdvancedFilterChange('version', e.target.value)}>
                                <option value="">Any version</option> <option value="1.8">1.8.x</option> <option value="1.12">1.12.x</option> <option value="1.16">1.16.x</option> <option value="1.19">1.19.x</option> <option value="1.20">1.20.x</option>
                            </select>
                        </div>
                        {/* Players */}
                        <div className="transform-gpu transition-medium hover:scale-[1.02]">
                            <label htmlFor="filter-players" className="block text-xs text-gray-400 mb-1">Player Count</label>
                            <select id="filter-players" className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-blue/50 hover:border-dark-300 transition-medium" value={playerCount} onChange={(e) => handleAdvancedFilterChange('players', e.target.value)}>
                                <option value="">Any size</option> <option value="small">Small (0-100)</option> <option value="medium">Medium (101-1000)</option> <option value="large">Large (1000+)</option>
                            </select>
                        </div>
                        {/* Tested */}
                        <div className="transform-gpu transition-medium hover:scale-[1.02]">
                            <label htmlFor="filter-tested" className="block text-xs text-gray-400 mb-1">Last Tested</label>
                            <select id="filter-tested" className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-blue/50 hover:border-dark-300 transition-medium" value={lastTested} onChange={(e) => handleAdvancedFilterChange('tested', e.target.value)}>
                                <option value="">Any time</option> <option value="1h">Last hour</option> <option value="24h">Last 24 hours</option> <option value="7d">Last 7 days</option> <option value="30d">Last 30 days</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServerFilter;